import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { getModelStatus, getModelDownloadLink, isConfigured } from '../../../services/kiriEngineService';

const KiriEngineScanLoader = ({ onModelLoaded, onClose }) => {
  const [serialNum, setSerialNum] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusInfo, setStatusInfo] = useState(null);

  const handleLoad = async (e) => {
    e?.preventDefault();
    if (!serialNum?.trim()) return;
    setLoading(true);
    setError('');
    setStatusInfo(null);

    try {
      // First check status
      const statusRes = await getModelStatus(serialNum?.trim());
      const status = statusRes?.data?.status;

      if (status !== 'completed' && status !== 'done' && status !== 2 && status !== '2') {
        setStatusInfo({ status, serialNum: serialNum?.trim() });
        setLoading(false);
        return;
      }

      // Get download link
      const dlRes = await getModelDownloadLink(serialNum?.trim());
      const downloadUrl = dlRes?.data?.downloadUrl || dlRes?.data?.url;

      if (!downloadUrl) {
        setError('Model is ready but no download URL was returned. Please try again.');
        setLoading(false);
        return;
      }

      onModelLoaded({ url: downloadUrl, serialNum: serialNum?.trim() });
    } catch (err) {
      setError(err?.message || 'Failed to load scan from Kiri Engine.');
    } finally {
      setLoading(false);
    }
  };

  if (!isConfigured()) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-card border border-border rounded-xl shadow-warm-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Box" size={16} color="var(--color-primary)" />
              </div>
              <h3 className="font-heading font-semibold text-foreground text-sm">Kiri Engine</h3>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-sm text-warning">
            Kiri Engine API key not configured. Add <code className="font-mono text-xs bg-warning/10 px-1 rounded">VITE_KIRI_ENGINE_API_KEY</code> to your environment variables.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-warm-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Box" size={16} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground text-sm">Load from Kiri Engine</h3>
              <p className="text-xs text-muted-foreground">Enter your scan serial number</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleLoad} className="p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Serial Number
            </label>
            <input
              type="text"
              value={serialNum}
              onChange={(e) => setSerialNum(e?.target?.value)}
              placeholder="e.g. KE-XXXXXXXX"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              disabled={loading}
            />
          </div>

          {statusInfo && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="Clock" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-warning">Scan still processing</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Status: <span className="font-mono">{statusInfo?.status}</span>. Please wait for processing to complete.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={14} color="var(--color-destructive)" className="mt-0.5 flex-shrink-0" />
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={loading || !serialNum?.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Icon name="Download" size={14} />
                  Load Scan
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KiriEngineScanLoader;
